import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from "react-redux";
import LiveVideoComponent from "../LiveVideoComponent";
import SpinnerComponent from "../../reusable/SpinnerComponent";
import UnSupportedFormatComponent from "../../reusable/UnSupportedFormatComponent";
import { ApplicationState } from "../../../store";
import { CamerasState } from "../../../store/cameras/types";
import { setCameraStreamName } from "../../../store/cameras/actions";
import { APIState } from "../../../store/API/types";
import service from "../../../services/ocularis/ocularisService";
import './style.scss';
import NoVideoComponent from '../../reusable/NoVideoComponent';
import useIsMounted from '../../../hooks/useIsMounted';
import { chooseService, getLocalBounds } from '../../../utils';
import { LayoutState } from '../../../store/layouts/types';

interface Props {
  pane: number,
  stream: any,
  cameraId: string
}

const LiveGetStreamComponent: React.FC<Props> = ({pane, stream, cameraId}) => {
  const dispatch = useDispatch();
  const ref = useRef<HTMLDivElement>(null);
  const {streamStatuses, currentCameras} = useSelector<ApplicationState, CamerasState>(state => state.cameras);
  const {expandedVideoLayout} = useSelector<ApplicationState, LayoutState>(state => state.layouts);
  const {token} = useSelector<ApplicationState, APIState>(state => state.API);
  const [liveIndicator, setLiveIndicator] = useState<string>('init');
  const reload = currentCameras[pane] && currentCameras[pane].reload;
  const isMounted = useIsMounted();

  const streamStatus = streamStatuses[pane];
  let innerComponent;

  const doDisconnect = () => {
    if (streamStatus && streamStatus.requestId) {
      service.cancelRequest(streamStatus.requestId);
      service.disconnectPane(token, pane);
    }
  };

  const setVideoIndicator = (status: string) => {
    if (isMounted.current) {
      setLiveIndicator(status);
    }
  }

  const getStreamName = () => {
    chooseService(() => {
      const requestId = service.generateId();
      const {stream_id, width, height} = stream;
      dispatch(setCameraStreamName({cameraId: cameraId, stream_id, token, width, height, pane, requestId}));
    }, () => {
      const requestId = service.generateId();
      const el = ref.current;
      if (el) {
        const {width, height} = getLocalBounds(el);
        dispatch(setCameraStreamName({cameraId, stream_id: null, token, width, height, pane, requestId}));
      }
    });
  }

  useEffect(() => {
    if (reload >= 0) {
      getStreamName();
      return () => {
        doDisconnect();
      }
    }
  }, [reload]);

  useEffect(() => {
    if (reload !== 0) {
      getStreamName();
      return () => {
        doDisconnect();
      }
    }
  }, [stream]);

  useEffect(() => {
    if (expandedVideoLayout === 0) {
      getStreamName();
      return () => {
        doDisconnect();
      }
    }
  }, [stream]);

  useEffect(() => {
    if (liveIndicator === 'no-data') {
      getStreamName();
      return () => {
        doDisconnect();
      }
    }
  }, [liveIndicator]);

  if (!streamStatus) return <div className='liveVideoContainer' ref={ref}></div>;
  const {status, data} = streamStatus;

  switch (status) {
    case 'fetch': {
      innerComponent = <SpinnerComponent/>;
      break;
    }
    case 'success': {
      innerComponent = <LiveVideoComponent streamName={data} pane={pane} setVideoIndicator={setVideoIndicator}/>;
      break;
    }
    case 'error': {
      innerComponent = <UnSupportedFormatComponent data={data}/>;
      break;
    }
    case 'offline': {
      innerComponent = <NoVideoComponent/>;
      break;
    }
  }

  return <div className='liveVideoContainer' ref={ref}>
    {innerComponent}
  </div>;

};

export default LiveGetStreamComponent